/**
 * Accept.js integration for the DESK keyed-entry flow (#522).
 *
 * Adapted from the customer-app's lib/acceptjs.ts (#234) — apps do not
 * import across app boundaries, so the loader/dispatch/error-mapping
 * seam is duplicated here with STAFF-facing recovery copy (the customer
 * copy says "contact the front desk"; here the reader IS the front
 * desk).
 *
 * Authorize.net's Accept.js tokenizes card data IN THE BROWSER and
 * returns a single-use opaque nonce (`opaqueData`, 15-minute TTL). The
 * card number / CVV never touch our servers (SAQ A-EP). This module
 * owns:
 *
 *   1. **Lazy script loading by mode.** Accept.js must come from
 *      Authorize.net's CDN at runtime — NEVER bundled — and the URL
 *      differs by gateway mode:
 *        sandbox    → https://jstest.authorize.net/v1/Accept.js
 *        production → https://js.authorize.net/v1/Accept.js
 *      The mode arrives from the staff-authed
 *      `GET /api/v2/admin/payments/gateway-config` discovery endpoint;
 *      it is never hardcoded here.
 *
 *   2. **A promise wrapper over `Accept.dispatchData`** (the library
 *      itself is callback-based).
 *
 *   3. **E_WC_* error-code mapping** to friendly, field-level messages
 *      (Accept.js client-side validation errors).
 *
 * The loader is intentionally idempotent and window-global aware: if
 * `window.Accept` already exists (script previously loaded, or a dev/
 * test harness installed a fake) it resolves immediately without
 * injecting a second script tag.
 */

export type AcceptJsMode = 'sandbox' | 'production';

export const ACCEPT_JS_URLS: Record<AcceptJsMode, string> = {
  sandbox: 'https://jstest.authorize.net/v1/Accept.js',
  production: 'https://js.authorize.net/v1/Accept.js',
};

/** The browser-public tenant credentials Accept.dispatchData requires. */
export interface AcceptJsAuthData {
  /** Accept.js casing is `apiLoginID` (capital ID) — wire-aligned with gateway-config. */
  apiLoginID: string;
  clientKey: string;
}

/** Raw card fields — exist ONLY in browser memory, never posted to our API. */
export interface AcceptJsCardData {
  cardNumber: string;
  month: string;
  year: string;
  cardCode: string;
  zip?: string;
  fullName?: string;
}

/** The single-use nonce the server-side charge consumes (15-min TTL). */
export interface AcceptJsOpaqueData {
  dataDescriptor: string;
  dataValue: string;
}

export interface AcceptJsMessage {
  code: string;
  text: string;
}

export interface AcceptJsResponse {
  messages: {
    resultCode: 'Ok' | 'Error';
    message: AcceptJsMessage[];
  };
  opaqueData?: AcceptJsOpaqueData;
}

/** The global Accept.js installs on `window`. */
export interface AcceptStatic {
  dispatchData: (
    data: { authData: AcceptJsAuthData; cardData: AcceptJsCardData },
    callback: (response: AcceptJsResponse) => void,
  ) => void;
}

declare global {
  interface Window {
    Accept?: AcceptStatic;
  }
}

/** Thrown by `dispatchCardData` when Accept.js reports client-side errors. */
export class AcceptJsDispatchError extends Error {
  public readonly errors: AcceptJsMessage[];
  constructor(errors: AcceptJsMessage[]) {
    super(errors[0]?.text ?? 'Accept.js dispatch failed');
    this.name = 'AcceptJsDispatchError';
    this.errors = errors;
  }
}

// One in-flight/settled load promise per page. Accept.js installs a
// single `window.Accept` global, so sandbox + production cannot
// coexist — a tenant has exactly one mode at a time, enforced below.
let loadedMode: AcceptJsMode | null = null;
let loadPromise: Promise<AcceptStatic> | null = null;

/**
 * Lazily load Accept.js from the Authorize.net CDN for the given mode.
 *
 * - Rejects when a DIFFERENT mode was already loaded (or is loading) on
 *   this page — Accept.js installs ONE `window.Accept` global, so a
 *   sandbox script must never serve a production dispatch (or vice
 *   versa) after a mid-session mode flip. Only `loadedMode === null`
 *   (a dev/test harness installed a fake before any loader call) passes
 *   the `window.Accept` short-circuit unconditionally.
 * - Resolves immediately when `window.Accept` already exists and the
 *   mode does not conflict.
 * - Caches the promise: concurrent callers share one script tag.
 * - Rejects (and clears the cache so a retry can re-attempt) when the
 *   script fails to load.
 */
export function loadAcceptJs(mode: AcceptJsMode): Promise<AcceptStatic> {
  if (loadedMode !== null && loadedMode !== mode) {
    // Surfaces as the keyed-entry "unavailable" panel — honest: the
    // page is in a cross-mode state and must be reloaded.
    return Promise.reject(
      new Error(
        `Accept.js already loaded in '${String(loadedMode)}' mode; cannot also load '${mode}'.`,
      ),
    );
  }
  if (typeof window !== 'undefined' && window.Accept && loadedMode === null) {
    // Pre-installed global (dev/test harness fake) — no script needed.
    return Promise.resolve(window.Accept);
  }
  if (loadPromise) {
    return loadPromise;
  }

  loadedMode = mode;
  loadPromise = new Promise<AcceptStatic>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = ACCEPT_JS_URLS[mode];
    script.async = true;
    script.onload = () => {
      if (window.Accept) {
        resolve(window.Accept);
      } else {
        loadedMode = null;
        loadPromise = null;
        reject(new Error('Accept.js loaded but window.Accept is missing.'));
      }
    };
    script.onerror = () => {
      loadedMode = null;
      loadPromise = null;
      script.remove();
      reject(new Error('Failed to load the Accept.js payment library.'));
    };
    document.head.appendChild(script);
  });
  return loadPromise;
}

/**
 * Promise wrapper over `Accept.dispatchData`. Resolves with the
 * opaqueData nonce; rejects with `AcceptJsDispatchError` carrying the
 * E_WC_* messages on client-side validation/processing failure.
 */
export function dispatchCardData(
  accept: AcceptStatic,
  authData: AcceptJsAuthData,
  cardData: AcceptJsCardData,
): Promise<AcceptJsOpaqueData> {
  return new Promise<AcceptJsOpaqueData>((resolve, reject) => {
    accept.dispatchData({ authData, cardData }, (response) => {
      if (
        response.messages.resultCode === 'Ok' &&
        response.opaqueData !== undefined
      ) {
        resolve(response.opaqueData);
        return;
      }
      reject(new AcceptJsDispatchError(response.messages.message ?? []));
    });
  });
}

// ────────────────────────────────────────────────────────────────────────
// E_WC_* error-code mapping (Accept.js client-side error codes)
// ────────────────────────────────────────────────────────────────────────

export type AcceptJsField = 'cardNumber' | 'expiry' | 'cvv' | 'name' | 'zip';

export interface AcceptJsErrorInfo {
  /**
   * - `field`     → attach the message to a specific input (or all, when
   *                 `field` is absent — E_WC_04 "mandatory fields").
   * - `config`    → the tenant's payment setup is broken (e.g. E_WC_21 wrong
   *                 credentials — the canary for tenant-cred mix-ups; E_WC_02
   *                 non-HTTPS page). Retrying with the same page won't help.
   * - `transient` → tokenization hiccup; safe to try again (NO charge was
   *                 attempted — the nonce never existed).
   */
  kind: 'field' | 'config' | 'transient';
  field?: AcceptJsField;
  message: string;
}

const FIELD_ERRORS: Record<string, { field?: AcceptJsField; message: string }> =
  {
    E_WC_04: { message: 'Please fill in all card fields.' },
    E_WC_05: {
      field: 'cardNumber',
      message: 'Please check the card number.',
    },
    E_WC_06: { field: 'expiry', message: 'Please check the expiration month.' },
    E_WC_07: { field: 'expiry', message: 'Please check the expiration year.' },
    E_WC_08: {
      field: 'expiry',
      message: 'This card has expired — the expiration date must be in the future.',
    },
    E_WC_15: { field: 'cvv', message: 'Please check the security code (CVV).' },
    E_WC_16: { field: 'zip', message: 'Please check the ZIP code.' },
    E_WC_17: { field: 'name', message: 'Please check the cardholder name.' },
  };

const CONFIG_ERRORS: Record<string, string> = {
  // Script include / load problems.
  E_WC_01:
    'The secure payment library did not load correctly. Refresh the page and try again — the card was not charged.',
  E_WC_03:
    'The secure payment library did not load correctly. Refresh the page and try again — the card was not charged.',
  // HTTPS required — bites local dev (issue #234 note).
  E_WC_02:
    'Card payments require a secure (HTTPS) connection. This should never appear on the live admin site — contact support.',
  // Missing/invalid public credentials. E_WC_21 is the wrong-tenant-creds
  // canary: authentication failed against Authorize.net with the values
  // gateway-config served.
  E_WC_18:
    'Card payment setup for this facility is not working — the card was not charged. An owner can re-enter the gateway credentials under Payments → Gateway credentials.',
  E_WC_21:
    'Card payment setup for this facility is not working — the card was not charged. An owner can re-enter the gateway credentials under Payments → Gateway credentials.',
};

/**
 * Map an Accept.js E_WC_* code to a friendly, actionable message.
 * Unknown codes are treated as transient (tokenization failed before
 * any charge existed — retrying is safe).
 */
export function describeAcceptJsError(code: string): AcceptJsErrorInfo {
  const fieldErr = FIELD_ERRORS[code];
  if (fieldErr) {
    return {
      kind: 'field',
      ...(fieldErr.field !== undefined ? { field: fieldErr.field } : {}),
      message: fieldErr.message,
    };
  }
  const configMsg = CONFIG_ERRORS[code];
  if (configMsg !== undefined) {
    return { kind: 'config', message: configMsg };
  }
  return {
    kind: 'transient',
    message:
      'Something went wrong preparing the card details. No charge was made — please try again.',
  };
}

/** Test-only: reset the module-level loader cache between tests. */
export const __acceptJsTestOnly__ = {
  reset(): void {
    loadedMode = null;
    loadPromise = null;
  },
};
